import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Alert } from '@/components/ui/Alert'
import { orderService } from '@/services/orders'

export default function OrderConfirmation() {
  const { orderId } = useParams<{ orderId: string }>()

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => orderService.getOrder(parseInt(orderId || '0')),
    enabled: !!orderId,
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="max-w-2xl mx-auto py-8">
        <Alert variant="error">
          Order not found. Please check your order number or contact support.
        </Alert>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto py-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-10 h-10 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
        <p className="text-gray-600">
          Thank you for your order. We've sent a confirmation email to your inbox.
        </p>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold">Order #{order.order_number}</h2>
            <p className="text-gray-500">{formatDate(order.created_at)}</p>
          </div>
          <Badge variant="success">{order.status.toUpperCase()}</Badge>
        </div>

        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3">Order Items</h3>
          {order.items?.map((item, index) => (
            <div key={index} className="flex justify-between py-2 border-b last:border-0">
              <div>
                <p className="font-medium">{item.product_name}</p>
                <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
              </div>
              <p className="font-medium">{formatCurrency(item.total_price)}</p>
            </div>
          ))}
        </div>

        <div className="border-t mt-4 pt-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-{formatCurrency(order.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>VAT (20%)</span>
              <span>{formatCurrency(order.vat_amount)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span>{formatCurrency(order.shipping_cost)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total</span>
              <span>{formatCurrency(order.total_amount)}</span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="font-semibold mb-3">Delivery Address</h3>
        <p>{order.shipping_address_line1}</p>
        {order.shipping_address_line2 && <p>{order.shipping_address_line2}</p>}
        <p>{order.shipping_city}, {order.shipping_county}</p>
        <p>{order.shipping_postcode}</p>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="font-semibold mb-3">What's Next?</h3>
        <ul className="space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-silux-primary font-bold">1.</span>
            <span>You will receive an email confirmation with your order details.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-silux-primary font-bold">2.</span>
            <span>We will process your order within 1-2 business days.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-silux-primary font-bold">3.</span>
            <span>You will receive shipping information once your order is dispatched.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-silux-primary font-bold">4.</span>
            <span>Track your order status in your account dashboard.</span>
          </li>
        </ul>
      </Card>

      <div className="flex justify-center gap-4">
        <Link to="/shop">
          <Button variant="outline">Continue Shopping</Button>
        </Link>
        <Link to="/account/orders">
          <Button variant="primary">View My Orders</Button>
        </Link>
      </div>
    </div>
  )
}
